import type { StateData } from '@/lib/become-an-agent-types';

function formatCurrency(n: number | null): string {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatNumber(n: number | null): string {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString('en-US');
}

export function StateFactsPanel({ state }: { state: StateData }) {
    const blsYear = state.bls_data_year ?? '';

    return (
        // Lighter teal than the hero — subtle tonal step that signals "this is
        // the next section" without breaking the dark-block rhythm. Tight pt
        // so the headline lifts into the bottom of the hero's viewport.
        <section id="state-regulations" className="scroll-mt-20 bg-[#0e2727]">
            <div className="mx-auto max-w-6xl px-6 py-10 sm:py-12 lg:py-14">
                <h2 className="font-didot text-3xl text-[#F5F5F5] sm:text-4xl">
                    Do you need a license to be a travel agent in {state.state}?
                </h2>
                <p
                    className="font-didot mt-5 max-w-3xl text-[#F5F5F5]/85 italic [&_strong]:not-italic [&_strong]:font-semibold [&_strong]:text-[#F5F5F5]"
                    style={{
                        fontSize: 'clamp(1.15rem, 1.6vw, 1.45rem)',
                        lineHeight: 1.45,
                        letterSpacing: '0.005em',
                    }}
                >
                    {state.sot_required ? (
                        <>
                            <strong>Yes.</strong> {state.state} requires a Seller of Travel
                            registration
                            {state.sot_program_name ? ` (the ${state.sot_program_name})` : ''}
                            {state.sot_fee_dollars !== null
                                ? `, with an annual fee of ${formatCurrency(state.sot_fee_dollars)}`
                                : ''}
                            . Joining a host agency that already holds the license removes that
                            burden from the individual agent.
                        </>
                    ) : (
                        <>
                            <strong>No.</strong> {state.state} does not require a Seller of Travel
                            registration to sell travel commercially. State-level rules may still
                            apply if you sell to residents of California, Florida, Hawaii, or
                            Washington (regardless of where you live).
                        </>
                    )}
                </p>
                <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {/* SOT licensing */}
                    <div className="rounded-sm border border-[#F5F5F5]/[0.08] p-6">
                        <div className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/60">
                            Seller of Travel license
                        </div>
                        <div className="font-didot text-2xl text-[#F5F5F5]">
                            {state.sot_required ? 'Required' : (
                                <>
                                    Not required
                                    <a href="#sot-client-residency-note" className="text-[#F5F5F5]/60 no-underline">*</a>
                                </>
                            )}
                        </div>
                        {state.sot_required && state.sot_fee_dollars !== null && (
                            <div className="mt-2 text-sm text-[#F5F5F5]/75">
                                Registration: {formatCurrency(state.sot_fee_dollars)}/yr
                            </div>
                        )}
                        {state.sot_regulator_url && (
                            <a
                                href={state.sot_regulator_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-block text-xs text-[#F5F5F5]/55 underline-offset-2 hover:underline"
                            >
                                Source: {state.sot_regulator_name ?? 'State regulator'} ↗
                            </a>
                        )}
                    </div>

                    {/* Educational requirement */}
                    <div className="rounded-sm border border-[#F5F5F5]/[0.08] p-6">
                        <div className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/60">
                            Education requirement
                        </div>
                        <div className="font-didot text-2xl text-[#F5F5F5]">None</div>
                        <div className="mt-2 text-sm text-[#F5F5F5]/75">
                            No state-mandated degree or certification.
                        </div>
                    </div>

                    {/* BLS employment */}
                    <div className="rounded-sm border border-[#F5F5F5]/[0.08] p-6">
                        <div className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/60">
                            Travel agents in {state.state}
                        </div>
                        <div className="font-didot text-2xl text-[#F5F5F5]">
                            ~{formatNumber(state.bls_employment)}
                        </div>
                        {blsYear && state.bls_employment !== null && (
                            <div className="mt-3 text-xs text-[#F5F5F5]/55">
                                Source: BLS OEWS, May {blsYear}
                            </div>
                        )}
                    </div>

                    {/* BLS mean wage */}
                    <div className="rounded-sm border border-[#F5F5F5]/[0.08] p-6">
                        <div className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/60">
                            Mean annual wage
                        </div>
                        <div className="font-didot text-2xl text-[#F5F5F5]">
                            {formatCurrency(state.bls_mean_wage)}
                        </div>
                        {blsYear && state.bls_mean_wage !== null && (
                            <div className="mt-3 text-xs text-[#F5F5F5]/55">
                                Source: BLS OEWS, May {blsYear}
                            </div>
                        )}
                    </div>
                </div>

                {state.bls_employment === null && (
                    <p className="mt-6 text-sm text-[#F5F5F5]/65">
                        BLS does not publish state-level data for {state.state} due to sample-size suppression.
                    </p>
                )}

                {state.regulatory_note && (
                    <aside className="mt-8 border-l-2 border-[#F5F5F5]/30 pl-5">
                        <div className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/55">
                            {state.state} regulatory note
                        </div>
                        <p className="text-sm text-[#F5F5F5]/80 [&_strong]:font-semibold [&_strong]:text-[#F5F5F5]">
                            {state.regulatory_note}
                        </p>
                        {state.regulatory_note_source_url && (
                            <a
                                href={state.regulatory_note_source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-block text-xs text-[#F5F5F5]/55 underline-offset-2 hover:underline"
                            >
                                Source: {state.regulatory_note_source_label ?? 'Statutory reference'} ↗
                            </a>
                        )}
                    </aside>
                )}

                <p
                    id="sot-client-residency-note"
                    className="mt-8 border-t border-[#F5F5F5]/15 pt-6 text-sm text-[#F5F5F5]/65"
                >
                    {!state.sot_required && <span className="text-[#F5F5F5]/55">* </span>}
                    If you plan to sell to clients living in California, Florida, Washington, or Hawaii,
                    those states&rsquo; Seller of Travel rules may apply to you regardless of where you
                    operate.
                </p>

                <p className="mt-4 text-sm text-[#F5F5F5]/65 [&_strong]:font-semibold [&_strong]:text-[#F5F5F5]">
                    BLS pools agents across every career stage and arrangement. Among hosted advisors
                    specifically, full-time advisors with 3&ndash;5 years averaged{' '}
                    <strong>$66,400 in 2024</strong> and 15+ year veterans averaged{' '}
                    <strong>$113,000</strong>. LuxRally&rsquo;s luxury focus places
                    our agents in the top tier &mdash; <strong>full-time luxury hosted advisors averaged $156,550
                    in 2024</strong>.
                    <span className="block pt-1 text-xs font-normal text-[#F5F5F5]/50">
                        Source: <em>2025 Hosted Travel Advisor Report</em>, Host Agency Reviews.
                    </span>
                </p>
            </div>
        </section>
    );
}
